import { create } from "zustand";

import { ApiError, UNREACHABLE } from "../../api/client";
import type {
  PromotionBlocker,
  PromotionReason,
  PromotionState,
  PromotionStatus,
  PromotionStepId,
  PromotionSummary,
} from "../../api/types";
import { k, locale, t } from "../../i18n/i18n";
import { plural } from "../../plural";

/** Promotion de la préproduction vers la production (registre F35, docs/11 §11) : logique pure de l'encadré de
 *  l'accueil et de la fenêtre (PromotionPanel.tsx, PromotionDialog.tsx). Le client ne montre jamais le message du
 *  serveur : il traduit les codes du contrat (refus, motifs, empêchements). Les versions arrivent brutes
 *  (`AAAAMMJJhhmmss-révision`, heure UTC de la mise en ligne) et s'affichent en date, à l'heure de Paris. */

/** Résumé de la promotion dans le cache de react-query (encadré, bandeau, fenêtre). */
export const PROMOTION_KEY = ["preprod", "promotion"] as const;

/** Étapes montrées, dans l'ordre ; `rollback` s'y ajoute en cas d'échec. */
export const STEP_ORDER: readonly PromotionStepId[] = ["checks", "backup", "switch", "database", "restart", "verify"];

export const STEP_LABELS: Record<PromotionStepId, string> = {
  checks: k("Vérifications préalables"),
  backup: k("Sauvegarde de la production"),
  switch: k("Mise en ligne de la version"),
  database: k("Mise à jour de la base"),
  restart: k("Redémarrage"),
  verify: k("Contrôles de la production"),
  rollback: k("Retour à la version précédente"),
};

/** États finaux : le suivi s'arrête. */
export const FINAL_STATES: ReadonlySet<PromotionState> = new Set([
  "succeeded",
  "refused",
  "aborted",
  "failed_restored",
  "failed_unrestored",
  "interrupted_restored",
]);

export const isFinal = (state: PromotionState | null | undefined): boolean => Boolean(state && FINAL_STATES.has(state));
/** Promotion engagée (demande déposée ou en cours sur le serveur). */
export const isActive = (state: PromotionState | null | undefined): boolean => state === "pending" || state === "running";

/** Rythmes du suivi : 1 s tant que le service n'a pas pris la demande, 1,5 s pendant la promotion, 5 s fenêtre
 *  fermée ; rien une fois la promotion finie. */
export const POLL_PENDING_MS = 1000;
export const POLL_RUNNING_MS = 1500;
export const POLL_CLOSED_MS = 5000;

export function pollDelay(state: PromotionState | null | undefined, dialogOpen: boolean): number | null {
  if (!isActive(state)) return null;
  if (!dialogOpen) return POLL_CLOSED_MS;
  return state === "pending" ? POLL_PENDING_MS : POLL_RUNNING_MS;
}

/** Étape de la fenêtre : `sending` (demande en route vers l'API), puis `checking` (demande déposée, mot de passe pas
 *  encore vérifié par le service de promotion : la fenêtre reste sur la saisie), puis le suivi (`running`) seulement
 *  une fois le mot de passe accepté. */
export type DialogPhase =
  | "ready"
  | "sending"
  | "checking"
  | "refused"
  | "running"
  | "succeeded"
  | "failed_restored"
  | "failed_unrestored"
  | "interrupted_restored"
  | "aborted";

/** Du statut (et de l'envoi en cours) à l'étape de la fenêtre. */
export function dialogPhase(status: PromotionStatus | null | undefined, sending: boolean): DialogPhase {
  if (sending) return "sending";
  if (!status) return "ready";
  if (status.state === "pending") return "checking";
  if (status.state === "running") return "running";
  return status.state as Exclude<PromotionState, "pending" | "running">;
}

// --- Versions -----------------------------------------------------------------------------------------------------

const BUILD = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})-/;

/** Instant de la mise en ligne d'une version (`AAAAMMJJhhmmss-révision`, UTC), null si le numéro n'est pas daté. */
export function buildDate(build: string | null | undefined): Date | null {
  const m = BUILD.exec((build ?? "").trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m.map(Number) as number[];
  const date = new Date(Date.UTC(y!, mo! - 1, d!, h!, mi!, s!));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Jour d'un instant à l'heure de Paris, au format de la langue. */
const PARIS_DAY: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Paris" };

/** Date et heure d'un instant, à l'heure de Paris : « 05/10/2026 à 21:42 ». */
export function formatParis(at: Date | string | null | undefined): string {
  const date = typeof at === "string" ? new Date(at) : at;
  if (!date || Number.isNaN(date.getTime())) return "—";
  const day = date.toLocaleDateString(locale(), PARIS_DAY);
  const time = date.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" });
  return t("{date} à {time}", { date: day, time });
}

/** Jour seul, à l'heure de Paris (« 05/10/2026 ») ; un texte illisible reste tel quel. */
export function formatParisDay(at: string | null | undefined): string {
  const date = at ? new Date(at) : null;
  if (!date || Number.isNaN(date.getTime())) return at ?? "—";
  return date.toLocaleDateString(locale(), PARIS_DAY);
}

/** Heure seule, à l'heure de Paris (« 22:15 »). */
export function formatParisTime(at: string | null | undefined): string {
  const date = at ? new Date(at) : null;
  if (!date || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" });
}

/** Version en clair (« 05/10/2026 à 21:42 ») ; un numéro non daté (« dev ») reste tel quel. */
export function formatBuild(build: string | null | undefined): string {
  const date = buildDate(build);
  return date ? formatParis(date) : (build ?? "").trim() || "—";
}

/** Empreinte de la version (contenu exact, `manifest_sha256`), courte et groupée : « 1a2b 3c4d 5e6f ». La même
 *  s'affiche à la fin du déploiement en préproduction. */
export function formatFingerprint(sha: string | null | undefined): string {
  const head = (sha ?? "").trim().toLowerCase().slice(0, 12);
  return /^[0-9a-f]{12}$/.test(head) ? head.replace(/(.{4})(?=.)/g, "$1 ") : "—";
}

// --- Saisie -------------------------------------------------------------------------------------------------------

/** Mot de passe de promotion : mêmes bornes que le serveur (12 à 128 signes), saisi tel quel (espaces compris). */
export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 128;

/** Nombre de signes du mot de passe saisi (forme NFC, comme le serveur le compte). */
export function passwordLength(value: string): number {
  return [...value.normalize("NFC")].length;
}

/** Ce qui manque encore au mot de passe (désigné au clic sur « Promouvoir en production ») : rien de saisi, ou trop
 *  court ; null s'il est prêt à partir. Le serveur seul dit s'il est juste. */
export function missingField(password: string): "empty" | "short" | null {
  if (password === "") return "empty";
  if (passwordLength(password) < PASSWORD_MIN) return "short";
  return null;
}

/** Indication montrée quand le mot de passe manque ou est trop court. */
export function missingText(missing: "empty" | "short"): string {
  return missing === "empty"
    ? t("Saisissez le mot de passe de promotion.")
    : t("Le mot de passe de promotion compte au moins {n} signes.", { n: PASSWORD_MIN });
}

// --- Textes -------------------------------------------------------------------------------------------------------

/** Durée de coupure estimée : « environ 30 secondes », « environ 1 minute ». Le serveur la relève déjà quand la base
 *  a des mises à jour, comptées à part sur la ligne « Base de données ». */
export function formatDowntime(seconds: number): string {
  const s = Math.max(5, Math.round(seconds / 5) * 5);
  return s < 60
    ? t("environ {n} secondes", { n: s })
    : t("environ {text}", { text: plural(Math.round(s / 60), t("minute"), t("minutes")) });
}

/** Temps écoulé (« 1 min 05 s », « 42 s »). */
export function formatElapsed(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return t("{s} s", { s });
  return t("{m} min {s} s", { m: Math.floor(s / 60), s: String(s % 60).padStart(2, "0") });
}

/** Empêchement connu avant la demande, en clair (`lockedUntil` : fin d'un blocage après cinq mots de passe faux). */
export function blockerText(blocker: PromotionBlocker, lockedUntil?: string | null): string {
  switch (blocker) {
    case "locked":
      return lockedUntil
        ? t("Cinq mots de passe faux en une heure : promotions bloquées jusqu'à {time}, au Terminal aussi (retour manuel, déploiement direct).", { time: formatParisTime(lockedUntil) })
        : t("Cinq mots de passe faux en une heure : promotions bloquées pendant une heure, au Terminal aussi (retour manuel, déploiement direct).");
    case "not_configured":
      return t("Le mot de passe de promotion n'est pas encore défini sur le serveur : définissez-le depuis le Terminal du Mac (mode d'emploi d'exploitation, « Pré-production et promotion »).");
    case "same_build":
      return t("Rien à promouvoir : la production sert déjà cette version.");
    case "uncommitted_build":
      return t("Cette version a été déployée depuis un dépôt modifié non enregistré : elle ne peut pas passer en production.");
    case "unverified_build":
      return t("Cette version a été déployée sans les vérifications habituelles : elle ne peut pas passer en production.");
    case "production_unreachable":
      return t("La production ne répond pas : promotion impossible tant qu'elle n'est pas de nouveau en service.");
    case "running":
      return t("Une promotion est déjà en cours.");
    case "restore_running":
      return t("Une restauration de la production est en cours : promotion impossible.");
    case "preprod_deploying":
      return t("Une nouvelle version est en cours d'installation sur la préproduction : réessayez une fois l'installation finie.");
    case "production_deploying":
      return t("Un déploiement direct de la production est en cours ou s'est arrêté avant la fin : promotion impossible tant qu'il n'est pas terminé (mode d'emploi d'exploitation, « Déploiement direct »).");
    case "refreshing":
      return t("Les données de la préproduction sont en cours de recopie : réessayez dans quelques minutes.");
    case "nightly_backup":
      return t("La sauvegarde nocturne de la production est en cours ou va commencer : réessayez dans une heure.");
    case "snapshot_missing":
      return t("L'instantané de restauration de cette version n'est pas rangé sur le Mac : redéployez la préproduction pour le refaire.");
    case "schema_unknown":
      return t("La base de la production ne correspond à aucune version connue de cette préproduction : promotion impossible.");
    case "interrupted":
      return t("Une promotion précédente a été interrompue sans retour complet : intervention nécessaire depuis le Terminal du Mac (mode d'emploi d'exploitation, « Promotion interrompue »).");
    default:
      return t("Promotion impossible pour le moment.");
  }
}

/** Motif d'un refus, d'un abandon ou d'un échec, en clair (`status` : heure de fin d'un blocage, erreurs encore admises
 *  après un mot de passe faux). */
export function reasonText(
  reason: PromotionReason | null | undefined,
  status?: Partial<Pick<PromotionStatus, "locked_until" | "remaining">> | null,
): string {
  const until = status?.locked_until ? formatParisTime(status.locked_until) : null;
  const remaining = status?.remaining;
  switch (reason) {
    case "code":
      return typeof remaining === "number" && remaining > 0
        ? t("Mot de passe de promotion incorrect : encore {tries} avant un blocage d'une heure.", {
            tries: plural(remaining, t("essai"), t("essais")),
          })
        : t("Mot de passe de promotion incorrect : vérifiez-le, puis recommencez.");
    case "locked":
      return blockerText("locked", status?.locked_until);
    case "invalid":
      return t("Demande de promotion illisible : rechargez la page, puis recommencez.");
    case "expired":
      return t("La demande a mis trop de temps à parvenir au service de promotion : recommencez.");
    case "versions":
      return t("La préproduction ou la production a changé depuis l'ouverture de cette fenêtre : vérifiez les versions, puis recommencez.");
    case "promotion_running":
      return blockerText("running");
    case "nightly_backup":
      return until
        ? t("La sauvegarde nocturne de la production est en cours ou va commencer : réessayez après {time}.", { time: until })
        : blockerText("nightly_backup");
    case "disk":
      return t("Place insuffisante sur le serveur pour sauvegarder la production avant la promotion.");
    case "database_busy":
      return t("La base de la production est restée occupée : la production n'a pas changé. Réessayez dans un instant.");
    case "preprod_mismatch":
      return t("Les fichiers de la préproduction ne correspondent plus à la version installée : redéployez la préproduction, puis recommencez.");
    case "production_unhealthy":
      return t("La production ne répond pas normalement : promotion refusée, la production n'a pas changé.");
    case "migration_failed":
      return t("La mise à jour de la base de la production a échoué.");
    case "health_failed":
      return t("La production ne répondait pas correctement avec la nouvelle version.");
    case "copy_failed":
      return t("La copie de la version vers la production a échoué.");
    case "dependencies_failed":
      return t("L'installation des composants de la nouvelle version a échoué.");
    case "interrupted":
      return t("La promotion a été interrompue (arrêt ou redémarrage du serveur).");
    case "same_build":
    case "uncommitted_build":
    case "unverified_build":
    case "restore_running":
    case "preprod_deploying":
    case "production_deploying":
    case "refreshing":
    case "snapshot_missing":
    case "schema_unknown":
    case "not_configured":
      return blockerText(reason);
    default:
      return t("La promotion n'a pas abouti pour une raison imprévue : la production a été vérifiée, voyez le journal des promotions depuis le Terminal du Mac.");
  }
}

/** Refus de la demande par l'API (avant le service de promotion) : texte et champ à désigner. */
export function requestErrorText(error: unknown): { text: string; field?: "password" } {
  if (!(error instanceof ApiError)) return { text: t("La demande n'a pas pu être envoyée : réessayez dans un instant.") };
  const blocker = typeof error.detail?.blocker === "string" ? (error.detail.blocker as PromotionBlocker) : null;
  switch (error.code) {
    case UNREACHABLE:
      return { text: error.message };
    case "CSRF_REFUSED":
      return { text: t("Demande refusée par le contrôle de sécurité du navigateur : rechargez la page, puis recommencez.") };
    case "LICENCE_REQUIRED":
      return { text: t("Ce poste n'a plus de licence active : activez-le de nouveau, puis recommencez.") };
    case "LICENCE_READ_ONLY":
      return { text: t("Licence expirée : Generex est en lecture seule, la promotion est impossible.") };
    case "PROMOTION_ADMIN_ONLY":
      return { text: t("La promotion est réservée aux administrateurs de l'unité.") };
    case "PROMOTION_NOT_CONFIGURED":
      return { text: blockerText("not_configured") };
    case "PROMOTION_SAME_BUILD":
      return { text: blockerText("same_build") };
    case "PROMOTION_VERSIONS_CHANGED":
      return { text: reasonText("versions") };
    case "PROMOTION_RUNNING":
      return { text: blockerText("running") };
    case "PROMOTION_BLOCKED": {
      const until = typeof error.detail?.locked_until === "string" ? error.detail.locked_until : null;
      return { text: blocker ? blockerText(blocker, until) : t("Promotion impossible pour le moment.") };
    }
    case "PROMOTION_PASSWORD":
      return { text: t("Le mot de passe de promotion compte de {min} à {max} signes, sans retour à la ligne.", { min: PASSWORD_MIN, max: PASSWORD_MAX }), field: "password" };
    case "PROMOTION_SERVICE_UNAVAILABLE":
      return { text: t("Le service de promotion ne répond pas : la production n'a pas changé. Réessayez dans un instant.") };
  }
  if (error.status === 429) {
    const minutes = Math.max(1, Math.ceil((error.retryAfterMs ?? 300_000) / 60_000));
    return { text: t("Trop d'essais de promotion à la suite : patientez {duration}.", { duration: plural(minutes, t("minute"), t("minutes")) }) };
  }
  if (error.status === 422) return { text: t("Demande incomplète : vérifiez le mot de passe de promotion, puis recommencez."), field: "password" };
  if (error.status === 503) return { text: t("Le service de promotion ne répond pas : la production n'a pas changé. Réessayez dans un instant.") };
  return { text: t("La demande n'a pas pu être envoyée : réessayez dans un instant.") };
}

// --- Encadré de l'accueil -----------------------------------------------------------------------------------------

/** Empêchement montré sous les versions (le premier, hors « promotion en cours », montrée à part). */
export function panelBlocker(summary: Pick<PromotionSummary, "blockers" | "status">): PromotionBlocker | null {
  if (isActive(summary.status?.state)) return null;
  return summary.blockers.find((b) => b !== "running") ?? null;
}

/** Retour arrière en cours : la promotion a échoué, la production revient à sa version d'avant (jamais montré comme
 *  une « étape n sur 6 »). */
export function isRollingBack(status: Pick<PromotionStatus, "step" | "state"> | null | undefined): boolean {
  return isActive(status?.state) && status?.step === "rollback";
}

/** Rang de l'étape en cours (1 à 6) ; 1 tant que le service n'a pas pris la demande. */
export function stepNumber(status: Pick<PromotionStatus, "step"> | null | undefined): number {
  const index = status?.step ? STEP_ORDER.indexOf(status.step) : -1;
  return index < 0 ? 1 : index + 1;
}

export type PromotionStepState = PromotionStatus["steps"][number]["state"];

/** Étapes à montrer : les six, plus le retour arrière s'il a eu lieu (ou s'il est en cours). Le serveur envoie
 *  toujours les sept étapes : celle du retour arrière, restée « à faire » ou « sans objet », n'est pas montrée. */
export function shownSteps(status: PromotionStatus): { id: PromotionStepId; state: PromotionStepState }[] {
  const byId = new Map(status.steps.map((s) => [s.id, s]));
  const list: PromotionStepId[] = [...STEP_ORDER];
  const rollback = byId.get("rollback")?.state;
  if (status.step === "rollback" || (rollback && rollback !== "todo" && rollback !== "skipped")) list.push("rollback");
  return list.map((id) => ({ id, state: byId.get(id)?.state ?? (status.step === id ? "running" : "todo") }));
}

// --- Dernier résultat -----------------------------------------------------------------------------------------------

/** Durée pendant laquelle l'encadré rappelle le résultat d'une promotion finie fenêtre fermée. */
export const OUTCOME_KEEP_MS = 24 * 3600 * 1000;
const SEEN_KEY = "gx-promotion-resultat-vu";
let seenFallback: string | null = null;

/** Dernier résultat vu et masqué sur ce poste (commodité locale : jamais une donnée à garder). */
export function seenOutcome(): string | null {
  try {
    return window.localStorage.getItem(SEEN_KEY) ?? seenFallback;
  } catch {
    return seenFallback;
  }
}

/** Résultat vu (fenêtre fermée sur lui, ou rappel masqué) : l'encadré se met à jour aussitôt. */
export const useSeenOutcome = create<{ seen: string | null }>(() => ({ seen: null }));

export function markOutcomeSeen(requestId: string): void {
  seenFallback = requestId;
  useSeenOutcome.setState({ seen: requestId });
  try {
    window.localStorage.setItem(SEEN_KEY, requestId);
  } catch {
    // stockage indisponible (navigation privée) : gardé en mémoire le temps de la page
  }
}

/** Dernière demande déposée depuis la fenêtre de ce poste, avec son dernier statut connu. Le statut publié (lu par le
 *  résumé) ne la porte qu'une fois prise en charge par le service de promotion : d'ici là, et fenêtre fermée en cours
 *  de route, l'encadré montre cette demande (« Suivre », étape en cours) et non le résultat de la précédente. */
export const useTrackedRequest = create<{ status: PromotionStatus | null }>(() => ({ status: null }));

export function trackRequest(status: PromotionStatus | null): void {
  useTrackedRequest.setState({ status });
}

/** Statut montré par l'encadré : le statut publié, sauf demande plus récente déposée depuis ce poste (même demande :
 *  le publié, sauf s'il est resté en cours quand la fenêtre l'a déjà vue finir). Un refus suivi (mot de passe faux,
 *  bloqué…) n'est montré que dans la fenêtre : il n'efface jamais le dernier résultat publié (docs/11 §11.3), et
 *  l'accueil reste le même après un rechargement. */
export function panelStatus(
  published: PromotionStatus | null | undefined,
  tracked: PromotionStatus | null,
): PromotionStatus | null {
  if (!tracked || tracked.state === "refused") return published ?? null;
  if (!published) return tracked;
  if (published.request_id === tracked.request_id) {
    return isFinal(tracked.state) && !isFinal(published.state) ? tracked : published;
  }
  return Date.parse(tracked.started_at) > Date.parse(published.started_at) ? tracked : published;
}

/** Résultat à rappeler sur l'accueil : promotion finie depuis moins de 24 h, non masquée sur ce poste. Une production
 *  à rétablir a son encadré rouge permanent, à part. */
export function recentOutcome(
  status: PromotionStatus | null | undefined,
  now: number,
  seen: string | null,
): PromotionStatus | null {
  if (!status || !isFinal(status.state) || status.state === "failed_unrestored") return null;
  if (seen && seen === status.request_id) return null;
  const ended = status.ended_at ? Date.parse(status.ended_at) : NaN;
  if (Number.isNaN(ended) || now - ended > OUTCOME_KEEP_MS || ended - now > 300_000) return null;
  return status;
}

/** Ton et titre du rappel d'un résultat. */
export function outcomeTone(state: PromotionState): "ok" | "danger" | "warn" | "info" {
  if (state === "succeeded") return "ok";
  if (state === "failed_restored") return "danger";
  if (state === "interrupted_restored") return "warn";
  return "info";
}
